import React from 'react';
import Head from 'next/head';
import { ThemeProvider } from 'react-jss';
import { t, defineMessages } from '@sm/intl';

import useEndPageView from '~app/hooks/useEndPageView';
import theme, { useGlobalStyles } from '~app/styles/cmsTheme';
import { endpageVariants } from '~app/pages/SurveyThanks/constants';
import { setExperimentCookies } from '~app/pages/experiments';
import PageLayout from '~components/pageLayout';
import OneTrustFooterScript from '~components/oneTrustScript';
import { PageWithLayout, SurveyThanksServerSideProps } from '~helpers/pages/types';

export { default as getServerSideProps } from '~helpers/pages/survey-thanks/getServerSideProps';

const COPY = defineMessages({
  DEFAULT_THANKS_MESSAGE: {
    id: 'survey-thanks.thanksMessage',
    defaultMessage: 'Thank you for taking this survey.',
  },
  THANKS_PAGE_TITLE: {
    id: 'survey-thanks.title',
    defaultMessage: 'Thank you! Create Your Own Online Survey Now! | SurveyMonkey',
  },
  RESEARCH_PAGE_TITLE: {
    id: 'survey-thanks.research-title',
    defaultMessage: 'Research.net - Powerful tool for creating web surveys.  Online survey software made easy!',
  },
  NON_BRANDED_TITLE: {
    id: 'survey-thanks.non-branded-title',
    defaultMessage: 'Online Survey Software: Survey Thanks',
  },
});

const SurveyThanks: PageWithLayout<SurveyThanksServerSideProps> = ({
  endpageVariant,
  smParams,
  countryCode,
  countryRegion,
  experiments,
  canonicalHostname,
}) => {
  setExperimentCookies(experiments);

  useGlobalStyles();

  const Endpage = (): React.ReactElement => endpageVariants[endpageVariant];
  const titleVariants: { [key: string]: string } = {
    embeddedNonBranded: t(COPY.NON_BRANDED_TITLE),
    nonBranded: t(COPY.NON_BRANDED_TITLE),
    research: t(COPY.RESEARCH_PAGE_TITLE),
  };

  useEndPageView({
    countryCode,
    countryRegion,
    pageName: 'survey thanks',
    templateType: endpageVariant,
    surveyId: smParams?.surveyId,
    collectorId: smParams?.collectorId,
    respondentId: smParams?.respondentId,
    languageId: smParams?.languageId,
  });

  return (
    <ThemeProvider theme={theme}>
      <Head>
        <base target="_top" href="/" />
        <link rel="canonical" href={`${canonicalHostname}/survey-thanks`} />
        <title>{titleVariants[endpageVariant] ?? t(COPY.THANKS_PAGE_TITLE)}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Head>
      <Endpage />
      <OneTrustFooterScript />
    </ThemeProvider>
  );
};

SurveyThanks.getLayout = function getLayout(page) {
  const { layout, staticData, translationData } = page.props;

  return (
    <PageLayout layout={layout} staticData={staticData} translationData={translationData}>
      {page}
    </PageLayout>
  );
};

export default SurveyThanks;
